import type { Annotation, DrawingDocument, Point2D } from '../model';
import type { EditorSelection } from './editor';
import { copyFragment } from './clipboard';
import { createId, changeStructure } from './drawingDocumentCore';
import { remapStructureIds } from './structureIdRemap';
import { translateAnnotations } from './objectAlignment';
import { documentContentBounds } from './pageExtent';
import { validateDrawingDocument } from './drawingValidation';

/** A native subset: no session state, remote-save target, or new catalog identity. */
export function copySelectionDocument(document: DrawingDocument, selection: EditorSelection): DrawingDocument {
  const atoms = new Set(selection.atomIds);
  for (const edge of document.structure.graph.edges)
    if (selection.bondIds.includes(edge.id)) {
      atoms.add(edge.source);
      atoms.add(edge.target);
    }
  const fragment = copyFragment(document, [...atoms]);
  const annotations = document.annotations.filter((item) => selection.annotationIds?.includes(item.id));
  if (!atoms.size && !annotations.length) throw new Error('Select content to save as a template.');
  const bondIds = new Set(Object.keys(fragment?.bonds ?? {}));
  const annotationIds = new Set(annotations.map((item) => item.id));
  // Anchors must stay internal: do not silently drop a callout or retarget it on another paper.
  const check = (value: unknown, key = ''): void => {
    if (key === 'atomId' && (typeof value !== 'string' || !atoms.has(value)))
      throw new Error('Also select the atoms attached to this content.');
    if (key === 'bondId' && (typeof value !== 'string' || !bondIds.has(value)))
      throw new Error('Also select the bonds attached to this content.');
    if (key === 'annotationId' && (typeof value !== 'string' || !annotationIds.has(value)))
      throw new Error('Also select the shape attached to this callout.');
    if (Array.isArray(value)) {
      value.forEach((item) => check(item, key === 'atomIds' ? 'atomId' : key === 'bondIds' ? 'bondId' : ''));
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([field, item]) => check(item, field));
    }
  };
  for (const annotation of annotations) {
    if (!['shape', 'text', 'reaction-arrow', 'curved-arrow'].includes(annotation.kind)) {
      throw new Error('Templates support structures, shapes, reaction arrows, mechanism arrows, text and callouts.');
    }
    check(annotation);
  }
  const pick = <T>(values: Readonly<Record<string, T>>, ids: ReadonlySet<string>) =>
    Object.fromEntries(Object.entries(values).filter(([id]) => ids.has(id)));
  return validateDrawingDocument({
    schemaVersion: document.schemaVersion,
    structure: {
      ...document.structure,
      graph: {
        ...document.structure.graph,
        nodes: document.structure.graph.nodes.filter((node) => atoms.has(node.id)),
        edges: document.structure.graph.edges.filter((edge) => bondIds.has(edge.id))
      },
      atoms: fragment?.atoms ?? {},
      bonds: Object.fromEntries(Object.entries(fragment?.bonds ?? {}).map(([id, bond]) => [id, bond.properties])),
      formalCharges: fragment?.formalCharges ?? {},
      stereochemistry: { tetrahedral: fragment?.tetrahedral ?? {}, doubleBonds: fragment?.doubleBonds ?? {} }
    },
    depiction: {
      ...document.depiction,
      coordinateSet: {
        ...document.depiction.coordinateSet,
        coordinates: pick(document.depiction.coordinateSet.coordinates, atoms),
        ...(document.depiction.coordinateSet.depths
          ? { depths: pick(document.depiction.coordinateSet.depths, atoms) }
          : {})
      },
      atomLabels: pick(document.depiction.atomLabels, atoms),
      bondStyles: fragment?.bondStyles ?? {},
      bondLengthOverrides: pick(document.depiction.bondLengthOverrides, bondIds),
      atomAppearance: pick(document.depiction.atomAppearance ?? {}, atoms),
      bondAppearance: pick(document.depiction.bondAppearance ?? {}, bondIds),
      cycleFills: document.depiction.cycleFills?.filter((fill) => fill.atomIds.every((id) => atoms.has(id))) ?? []
    },
    annotations,
    derivedPropertySets: []
  });
}

/** Shared precommit insertion: pure, so preview/cancel never mutate history. Layout stays rigid. */
export function insertSelectionDocument(target: DrawingDocument, payload: DrawingDocument, at: Point2D) {
  const source = validateDrawingDocument(payload);
  let remapped = remapStructureIds(source, {
    atoms: Object.fromEntries(source.structure.graph.nodes.map((node) => [node.id, createId('atom')])),
    bonds: Object.fromEntries(source.structure.graph.edges.map((edge) => [edge.id, createId('bond')]))
  });
  const ids = new Map(source.annotations.map((item) => [item.id, createId('annotation')]));
  const remap = (value: unknown, key = ''): unknown => {
    if (key === 'annotationId' && typeof value === 'string') {
      const mapped = ids.get(value);
      if (!mapped) throw new Error('Template has a missing callout target.');
      return mapped;
    }
    if (key === 'coordinateSetId') return target.depiction.coordinateSet.id;
    if (Array.isArray(value)) return value.map((item) => remap(item));
    if (value && typeof value === 'object')
      return Object.fromEntries(Object.entries(value).map(([field, item]) => [field, remap(item, field)]));
    return value;
  };
  remapped = {
    ...remapped,
    annotations: remapped.annotations.map((item) => ({ ...(remap(item) as Annotation), id: ids.get(item.id)! }))
  };
  const bounds = documentContentBounds(remapped);
  const dx = at.x - (bounds ? (bounds.minX + bounds.maxX) / 2 : 0);
  const dy = at.y - (bounds ? (bounds.minY + bounds.maxY) / 2 : 0);
  remapped = translateAnnotations(remapped, new Set(ids.values()), dx, dy);
  const coordinates = Object.fromEntries(
    Object.entries(remapped.depiction.coordinateSet.coordinates).map(([id, point]) => [
      id,
      { x: point.x + dx, y: point.y + dy }
    ])
  );
  const changed = changeStructure(target, {
    graph: {
      ...target.structure.graph,
      revision: target.structure.graph.revision + 1,
      nodes: [...target.structure.graph.nodes, ...remapped.structure.graph.nodes],
      edges: [...target.structure.graph.edges, ...remapped.structure.graph.edges]
    },
    atoms: { ...target.structure.atoms, ...remapped.structure.atoms },
    bonds: { ...target.structure.bonds, ...remapped.structure.bonds },
    formalCharges: { ...target.structure.formalCharges, ...remapped.structure.formalCharges },
    stereochemistry: {
      tetrahedral: {
        ...target.structure.stereochemistry.tetrahedral,
        ...remapped.structure.stereochemistry.tetrahedral
      },
      doubleBonds: {
        ...target.structure.stereochemistry.doubleBonds,
        ...remapped.structure.stereochemistry.doubleBonds
      }
    }
  });
  const document = validateDrawingDocument({
    ...changed,
    derivedPropertySets: [],
    depiction: {
      ...changed.depiction,
      coordinateSet: {
        ...changed.depiction.coordinateSet,
        coordinates: { ...target.depiction.coordinateSet.coordinates, ...coordinates },
        depths: { ...target.depiction.coordinateSet.depths, ...remapped.depiction.coordinateSet.depths }
      },
      atomLabels: { ...target.depiction.atomLabels, ...remapped.depiction.atomLabels },
      bondStyles: { ...target.depiction.bondStyles, ...remapped.depiction.bondStyles },
      bondLengthOverrides: { ...target.depiction.bondLengthOverrides, ...remapped.depiction.bondLengthOverrides },
      atomAppearance: { ...target.depiction.atomAppearance, ...remapped.depiction.atomAppearance },
      bondAppearance: { ...target.depiction.bondAppearance, ...remapped.depiction.bondAppearance },
      cycleFills: [...(target.depiction.cycleFills ?? []), ...(remapped.depiction.cycleFills ?? [])]
    },
    annotations: [...target.annotations, ...remapped.annotations]
  });
  return {
    document,
    selection: {
      atomIds: remapped.structure.graph.nodes.map((node) => node.id),
      bondIds: remapped.structure.graph.edges.map((edge) => edge.id),
      annotationIds: [...ids.values()]
    }
  };
}

/** Explicit optional scaling for oversized templates; ordinary placement always keeps size. */
export function fitSelectionDocument(source: DrawingDocument, width: number, height: number): DrawingDocument {
  const bounds = documentContentBounds(source);
  if (!bounds) return source;
  const scale = Math.min(
    1,
    Math.max(1, width - 32) / Math.max(1, bounds.maxX - bounds.minX),
    Math.max(1, height - 32) / Math.max(1, bounds.maxY - bounds.minY)
  );
  if (scale === 1) return source;
  const dimensions = new Set(['x', 'y', 'width', 'height', 'fontSize', 'lineWidth', 'cornerRadius']);
  const transform = (value: unknown, key = ''): unknown => {
    if (typeof value === 'number') return dimensions.has(key) ? value * scale : value;
    if (Array.isArray(value)) return value.map((item) => transform(item));
    if (value && typeof value === 'object')
      return Object.fromEntries(Object.entries(value).map(([field, item]) => [field, transform(item, field)]));
    return value;
  };
  const coordinate = source.depiction.coordinateSet;
  return validateDrawingDocument({
    ...source,
    depiction: {
      ...source.depiction,
      coordinateSet: {
        ...coordinate,
        coordinates: transform(coordinate.coordinates),
        ...(coordinate.depths
          ? { depths: Object.fromEntries(Object.entries(coordinate.depths).map(([id, depth]) => [id, depth * scale])) }
          : {})
      }
    },
    annotations: source.annotations.map((item) => {
      const scaled = transform(item) as Annotation;
      if (scaled.kind === 'text' || (scaled.kind === 'shape' && scaled.callout)) {
        const paragraphs =
          item.kind === 'text' ? item.paragraphs : item.kind === 'shape' ? item.callout!.paragraphs : [];
        const scaledParagraphs = paragraphs.map((paragraph) => ({
          ...paragraph,
          runs: paragraph.runs.map((run) => ({ ...run, fontSize: (run.fontSize ?? 12) * scale }))
        }));
        return scaled.kind === 'text'
          ? { ...scaled, paragraphs: scaledParagraphs }
          : { ...scaled, callout: { ...scaled.callout!, paragraphs: scaledParagraphs } };
      }
      return scaled;
    })
  });
}
